import { format, lastDayOfMonth } from 'date-fns';
import { useMemo } from 'react';
import { MonthKey, periodsToMonthRange } from '../lib/period-tokens';
import { useGroupFilters } from './use-group-filters';

export type DateRange = {
  /** ISO YYYY-MM-DD start date, inclusive. Null = no lower bound. */
  start: string | null;
  /** ISO YYYY-MM-DD end date, inclusive. Null = no upper bound. */
  end: string | null;
};

const asDate = ({ year, month }: MonthKey): Date =>
  new Date(year, month - 1, 1);

/**
 * Converts the group dashboard's period picker selection (synthetic strings
 * like `year-2025` or `month-2025-03`) into a `{ start, end }` date range the
 * backend can filter on. The token grammar and min-start / max-end collapse
 * live in `../lib/period-tokens`; here we just widen the resulting month range
 * to full days — the first day of the start month and the last day of the end
 * month. Empty selection returns `{ start: null, end: null }`, which callers
 * pass through unchanged so the backend applies no date filter.
 *
 * Phase 2 will swap the mock picker for backend-driven group reporting periods;
 * this hook stays the single seam where periods become a date range.
 */
export function usePeriodDateRange(): DateRange {
  const [{ periods }] = useGroupFilters();
  return useMemo(() => {
    const range = periodsToMonthRange(periods ?? []);
    if (!range) return { start: null, end: null };
    return {
      start: format(asDate(range.start), 'yyyy-MM-dd'),
      end: format(lastDayOfMonth(asDate(range.end)), 'yyyy-MM-dd'),
    };
  }, [periods]);
}
